import type { DealInsightsGeneralStatsApi } from '@openx/types/dealInsights';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import type { TFunction } from 'i18next';
import { roundInventoryQualityScore } from '../../../../utils/formatInventoryQualityScore';
import type { StatsPanelConfig, StatsPanelItem, StatsPanelLabelConfig } from './types';
import {
  DEFAULT_STATS_PANEL_ORDER,
  GENERAL_STATS_PANEL_KEYS,
  getStatsPanelLabel,
  INVENTORY_QUALITY_PANEL_KEY,
} from './types';

const STORAGE_KEY = 'generalStatsSettings';

const applyStatsPanelValueFormat = (
  value: number | null | undefined,
  config: StatsPanelLabelConfig
): number | null | undefined => {
  if (value == null) {
    return value;
  }

  return config.roundToInteger ? roundInventoryQualityScore(value) : value;
};

export const saveStatsPanelConfig = (config: StatsPanelConfig[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // biome-ignore lint/suspicious/noConsole: logging info in console
    console.warn('Failed to save stats panel config');
  }
};

export const loadStatsPanelConfig = (): StatsPanelConfig[] | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const getDefaultStatsPanelConfig = (): StatsPanelConfig[] => {
  return DEFAULT_STATS_PANEL_ORDER.map(key => ({
    key,
    hide: false,
  }));
};

/** Adds IQS panel entry when missing; never removes it (preserves localStorage prefs). */
export const ensureInventoryQualityPanelConfig = (config: StatsPanelConfig[]): StatsPanelConfig[] => {
  if (config.some(item => item.key === INVENTORY_QUALITY_PANEL_KEY)) {
    return config;
  }

  return [...config, { key: INVENTORY_QUALITY_PANEL_KEY, hide: false }];
};

export const applyStatsPanelConfig = (
  items: StatsPanelItem[],
  config?: StatsPanelConfig[] | null
): StatsPanelItem[] => {
  if (!config) {
    return items;
  }

  const configMap = new Map(config.map(c => [c.key, c]));

  const configuredItems = items.map(item => ({
    ...item,
    hide: configMap.get(item.key)?.hide ?? false,
  }));

  const orderedItems = config
    .map(configItem => configuredItems.find(item => item.key === configItem.key))
    .filter((item): item is StatsPanelItem => item !== undefined);

  const remainingItems = configuredItems.filter(
    item => GENERAL_STATS_PANEL_KEYS.has(item.key) && !config.some(c => c.key === item.key)
  );

  return [...orderedItems, ...remainingItems];
};

export const buildStatsPanelItems = (
  stats: Partial<DealInsightsGeneralStatsApi> | undefined,
  t: TFunction
): StatsPanelItem[] => {
  return DEFAULT_STATS_PANEL_ORDER.map(key => {
    const entry = stats?.[key];
    const config = getStatsPanelLabel(key, t);
    const value = applyStatsPanelValueFormat(entry?.value, config);
    const isValueNullish = value == null;

    return {
      key,
      label: config.label,
      value,
      prefix: isValueNullish ? undefined : config.prefix,
      suffix: isValueNullish ? undefined : config.suffix,
      change: entry?.percentage_change ?? 0,
      changeLabel: abbreviateNumericValue(entry?.percentage_change, {
        decimalPlaces: 1,
        allowZero: true,
        multiplier: 100,
        nullValue: '0',
      }),
      hide: false,
    };
  });
};

export const toggleStatsPanelVisibility = (
  config: StatsPanelConfig[],
  key: keyof Omit<DealInsightsGeneralStatsApi, 'previous_period_calculated'>
): StatsPanelConfig[] => {
  return config.map(item => (item.key === key ? { ...item, hide: !item.hide } : item));
};

export const reorderStatsPanels = (
  config: StatsPanelConfig[],
  oldIndex: number,
  newIndex: number
): StatsPanelConfig[] => {
  const result = [...config];
  const [removed] = result.splice(oldIndex, 1);
  result.splice(newIndex, 0, removed);
  return result;
};
